/**
 * Swedish Holidays Utility
 * Calculates Swedish public holidays (röda dagar) for a given year
 */

import { addDays, getDay } from 'date-fns'

export interface SwedishHoliday {
  date: Date
  name: string
  isRedDay: boolean // True for "röd dag" (public holiday), false for notable days like Julafton
}

/**
 * Calculate Easter Sunday using the Anonymous Gregorian algorithm
 * https://en.wikipedia.org/wiki/Date_of_Easter#Anonymous_Gregorian_algorithm
 */
function getEasterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1 // 0-indexed
  const day = ((h + l - 7 * m + 114) % 31) + 1

  return new Date(year, month, day)
}

/**
 * Get Midsummer Eve (Friday between June 19-25)
 */
function getMidsummerEve(year: number): Date {
  // Midsummer Eve is the Friday between June 19 and June 25
  const june19 = new Date(year, 5, 19)
  const dayOfWeek = getDay(june19)
  // Friday = 5
  const daysUntilFriday = (5 - dayOfWeek + 7) % 7
  return addDays(june19, daysUntilFriday)
}

/**
 * Get All Saints' Day (Saturday between Oct 31 - Nov 6)
 */
function getAllSaintsDay(year: number): Date {
  // All Saints' Day is the Saturday between October 31 and November 6
  const oct31 = new Date(year, 9, 31)
  const dayOfWeek = getDay(oct31)
  // Saturday = 6
  const daysUntilSaturday = (6 - dayOfWeek + 7) % 7
  return addDays(oct31, daysUntilSaturday)
}

/**
 * Get all Swedish holidays for a given year
 */
export function getSwedishHolidays(year: number): SwedishHoliday[] {
  const easter = getEasterSunday(year)
  const midsummerEve = getMidsummerEve(year)
  const midsummerDay = addDays(midsummerEve, 1)
  const allSaintsDay = getAllSaintsDay(year)

  const holidays: SwedishHoliday[] = [
    // Fixed holidays
    { date: new Date(year, 0, 1), name: 'Nyårsdagen', isRedDay: true },
    { date: new Date(year, 0, 6), name: 'Trettondedag jul', isRedDay: true },
    { date: new Date(year, 4, 1), name: 'Första maj', isRedDay: true },
    { date: new Date(year, 5, 6), name: 'Sveriges nationaldag', isRedDay: true },
    { date: new Date(year, 11, 24), name: 'Julafton', isRedDay: false },
    { date: new Date(year, 11, 25), name: 'Juldagen', isRedDay: true },
    { date: new Date(year, 11, 26), name: 'Annandag jul', isRedDay: true },
    { date: new Date(year, 11, 31), name: 'Nyårsafton', isRedDay: false },

    // Easter-based holidays
    { date: addDays(easter, -2), name: 'Långfredagen', isRedDay: true },
    { date: easter, name: 'Påskdagen', isRedDay: true },
    { date: addDays(easter, 1), name: 'Annandag påsk', isRedDay: true },
    { date: addDays(easter, 39), name: 'Kristi himmelsfärdsdag', isRedDay: true },
    { date: addDays(easter, 49), name: 'Pingstdagen', isRedDay: true },

    // Midsummer
    { date: midsummerEve, name: 'Midsommarafton', isRedDay: false },
    { date: midsummerDay, name: 'Midsommardagen', isRedDay: true },

    // All Saints
    { date: allSaintsDay, name: 'Alla helgons dag', isRedDay: true },
  ]

  return holidays.sort((a, b) => a.date.getTime() - b.date.getTime())
}

/**
 * Check if a date is a Swedish holiday
 * Returns the holiday info if it is, null otherwise
 */
export function getHolidayForDate(date: Date): SwedishHoliday | null {
  const year = date.getFullYear()
  const holidays = getSwedishHolidays(year)

  return holidays.find(h =>
    h.date.getFullYear() === date.getFullYear() &&
    h.date.getMonth() === date.getMonth() &&
    h.date.getDate() === date.getDate()
  ) || null
}

/**
 * Check if a date is a Swedish "röd dag" (public holiday)
 */
export function isSwedishHoliday(date: Date): boolean {
  const holiday = getHolidayForDate(date)
  return holiday?.isRedDay ?? false
}

/**
 * Get holidays for a specific month
 */
export function getHolidaysForMonth(year: number, month: number): SwedishHoliday[] {
  const holidays = getSwedishHolidays(year)
  return holidays.filter(h => h.date.getMonth() === month)
}
